import React, {useEffect, useState} from 'react';
import {BellIcon, DocumentTextIcon, PencilSquareIcon, UserIcon} from "@heroicons/react/24/solid";
import {getAdminInfoAPI, updateAdminIdAPI, updatePasswordAPI, updatePushAPI} from '../../../services/admin';
import styles from './AdminSetting.module.css';
import UpdateTermPopUp from "../../../views/admin/popup/UpdateTermPopUp";
import UpdatePolicyPopUp from "../../../views/admin/popup/UpdatePolicyPopUp";
import UpdateChatBotPopUp from "../../../views/admin/popup/UpdateChatBotPopUp";
import Button from "../../common/button/Button";
import {PushSettings} from "../../../types/admin";
import UpdateEmailPopUp from "../../../views/admin/popup/UpdateEmailPopUp";
import {useAlert} from "../../common/error/AlertContext";
import UpdateRegisterPopUp from "../../../views/admin/popup/UpdateRegisterPopUp";

//어드민 설정
const AdminSetting = () => {
    const [data, setData] = useState({
        userName: '',
        email: '',
        uploadPush: false,
        projectConfirmPush: false,
        projectStatusPush: false,
        billingPush: false
    });
    const [loading, setLoading] = useState(true);
    const [isPolicyModalOpen, setIsPolicyModalOpen] = useState(false);
    const [isTermModalOpen, setIsTermModalOpen] = useState(false);
    const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
    const [isChatModalOpen, setIsChatModalOpen] = useState(false);
    const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
    const [isUserNameEditMode, setIsUserNameEditMode] = useState(false);
    const [userNameInput, setUserNameInput] = useState("");
    const [isPasswordEditMode, setIsPasswordEditMode] = useState(false);
    const [passwordInputs, setPasswordInputs] = useState({
        password: '',
        confirmPassword: ''
    });
    const handlePolicyModalOpen = () => setIsPolicyModalOpen(true);
    const handlePolicyModalClose = () => setIsPolicyModalOpen(false);
    const handleRegisterModalOpen = () => setIsRegisterModalOpen(true);
    const handleRegisterModalClose = () => setIsRegisterModalOpen(false);
    const handleTermModalOpen = () => setIsTermModalOpen(true);
    const handleTermModalClose = () => setIsTermModalOpen(false);
    // const handleChatModalOpen = () => setIsChatModalOpen(true);
    const handleChatModalClose = () => setIsChatModalOpen(false);
    const handleEmailModalOpen = () => setIsEmailModalOpen(true);
    const handleEmailModalClose = () => {
        setIsEmailModalOpen(false);
        fetchData();
    };
    const {showAlert} = useAlert();
    const {showConfirm} = useAlert();

    //어드민 비밀번호 수정
    const updatePassword = async () => {
        //확인 창
        showConfirm(
            "비밀번호를 변경하시겠습니까?",
            async () => {
                //비밀번호 조건 확인
                const passwordError = validatePassword(passwordInputs.password);
                if (passwordError) {
                    //조건 미달 시 에러
                    showAlert(passwordError, "error");
                    return;
                }
                //비밀번호, 비밀번호 확인 불일치할 시 에러
                if (passwordInputs.password !== passwordInputs.confirmPassword) {
                    showAlert("비밀번호가 일치하지 않습니다.", "error");
                    return;
                }
                try {
                    //비밀번호 변경 API
                    await updatePasswordAPI(passwordInputs.password);
                    showAlert("비밀번호가 변경되었습니다.", "info");
                    setIsPasswordEditMode(false);
                    setPasswordInputs({password: '', confirmPassword: ''});
                } catch (error) {
                    console.log(error);
                }
            }
        )
    }

    //ID 변경
    const updateUserName = async () => {
        //확인 창
        showConfirm(
            "ID를 변경하시겠습니까?",
            async () => {
                //ID 조건 확인
                if (userNameInput.length < 4 || userNameInput.length > 10) {
                    showAlert("ID는 4자 이상, 10자 이하여야 합니다", "error");
                    return;
                }
                try {
                    //ID 변경 API
                    await updateAdminIdAPI(userNameInput);
                    showAlert("ID가 변경되었습니다.", "info");
                    setIsUserNameEditMode(false);
                    setUserNameInput("");
                    await fetchData();
                } catch (error) {
                    console.log(error);
                }
            }
        )
    }

    //비밀번호 조건 확인
    const validatePassword = (password: string) => {
        //최소 8자
        const minLength = 8;
        //문자 사용
        const hasLetter = /[a-zA-Z]/.test(password);
        //숫자 사용
        const hasNumber = /[0-9]/.test(password);
        //특수기호 사용
        const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);

        //에러 메세지
        if (password.length < minLength) {
            return '비밀번호는 8자 이상이어야 합니다.';
        }
        if (!hasLetter) {
            return '비밀번호에 문자가 포함되어야 합니다.';
        }
        if (!hasNumber) {
            return '비밀번호에 숫자가 포함되어야 합니다.';
        }
        if (!hasSpecial) {
            return '비밀번호에 특수문자가 포함되어야 합니다.';
        }
        return null;
    };

    //비밀번호 입력 창
    const togglePasswordEdit = () => {
        setIsPasswordEditMode(!isPasswordEditMode);
        if (!isPasswordEditMode) {
            setPasswordInputs({password: '', confirmPassword: ''});
        }
    };

    //ID 입력 창
    const toggleUserNameEdit = () => {
        setIsUserNameEditMode(!isUserNameEditMode);
        if (!isUserNameEditMode) {
            setUserNameInput("");
        }
    }

    //ID 입력
    const handleUserNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newUserName = e.target.value;
        setUserNameInput(newUserName);
    }

    //비밀번호 이볅
    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {name, value} = e.target;
        setPasswordInputs(prev => ({
            ...prev,
            [name]: value
        }));
    };

    //푸시 설정 수정
    const handleCheck = async (checked: boolean, setting: PushSettings) => {
        setData(prev => {
            const updates = {
                uploadPush: prev.uploadPush,
                projectConfirmPush: prev.projectConfirmPush,
                projectStatusPush: prev.projectStatusPush,
                billingPush: prev.billingPush
            };
            //푸시별 설정
            switch (setting) {
                case 'UPLOAD': //자료 업로드
                    updates.uploadPush = checked;
                    break;
                case 'PROJECT_CONFIRM': //프로젝트 추가 요청 처리
                    updates.projectConfirmPush = checked;
                    break;
                case 'PROJECT_STATUS': //프로젝트 상태 변경
                    updates.projectStatusPush = checked;
                    break;
                case 'BILLING': //세금 계산서 발급
                    updates.billingPush = checked;
                    break;
            }
            return {...prev, ...updates};
        });

        try {
            //푸시 설정 API
            await updatePushAPI({pushSettings: setting, push: checked});
        } catch (error) {
            //API 오류 시 푸시 설정 버튼 되돌리기
            setData(prev => {
                const updates = {
                    uploadPush: prev.uploadPush,
                    projectConfirmPush: prev.projectConfirmPush,
                    projectStatusPush: prev.projectStatusPush,
                    billingPush: prev.billingPush
                };
                switch (setting) {
                    case 'UPLOAD':
                        updates.uploadPush = !checked;
                        break;
                    case 'PROJECT_CONFIRM':
                        updates.projectConfirmPush = !checked;
                        break;
                    case 'PROJECT_STATUS':
                        updates.projectStatusPush = !checked;
                        break;
                    case 'BILLING':
                        updates.billingPush = !checked;
                        break;
                }
                return {...prev, ...updates};
            });
        }
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            //어드민 설정 조회 API
            const response = await getAdminInfoAPI();
            setData(response);
        } catch (error) {
            console.error('관리자 정보 조회 중 오류:', error);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        fetchData();
    }, []);
    if (loading) {
        return <div className={styles.loading}>로딩중...</div>;
    }
    return (
        <div className={styles.container}>
            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <div className={styles.headerContent}>
                        <UserIcon className={styles.sectionIcon}/>
                        <h2 className={styles.sectionTitle}>계정</h2>
                    </div>
                </div>
                <div className={styles.cardContent}>
                    <div className={styles.grid}>
                        <div className={styles.item}>
                            <span className={styles.label}>ID</span>
                            {/*iD*/}
                            {isUserNameEditMode ? (
                                // ID 수정 시
                                <div className={styles.buttonSection}>
                                    {/*ID 입력*/}
                                    <input
                                        type="text"
                                        name="userName"
                                        value={userNameInput}
                                        onChange={handleUserNameChange}
                                        className={styles.passwordInput}
                                        placeholder="새 ID 입력"
                                    />
                                    {/*변경 버튼*/}
                                    <Button
                                        variant="primary"
                                        onClick={updateUserName}
                                        size={"small"}
                                    >
                                        변경
                                    </Button>
                                    {/*취소 버튼*/}
                                    <Button
                                        variant="secondary"
                                        onClick={toggleUserNameEdit}
                                        size={"small"}
                                    >
                                        취소
                                    </Button>
                                </div>
                            ) : (
                                <div className={styles.userNameContainer}>
                                    {/*ID 조회*/}
                                    <span className={styles.userName}>{data.userName}</span>
                                    {/*ID 수정 모드 버튼*/}
                                    <button
                                        className={styles.editButton}
                                        onClick={toggleUserNameEdit}
                                    >
                                        <PencilSquareIcon className={styles.editIcon}/>
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className={styles.item}>
                            <span className={styles.label}>이메일 주소</span>
                            <div className={styles.emailWrapper}>
                                {/*이메일 주소*/}
                                <span>{data.email}</span>
                                {/*이메일 수정 버튼*/}
                                <button
                                    className={styles.editButton}
                                    onClick={handleEmailModalOpen}
                                >
                                    <PencilSquareIcon className={styles.editIcon}/>
                                </button>
                            </div>
                        </div>
                        <div className={styles.item}>
                            <span className={styles.label}>비밀번호</span>
                            <div className={styles.passwordWrapper}>
                                {/*비밀번호 수정*/}
                                {isPasswordEditMode ? (
                                    <div className={styles.buttonSection}>
                                        {/*비밀번호 변경 버튼*/}
                                        <Button
                                            variant="primary"
                                            onClick={updatePassword}
                                            size={"small"}
                                        >
                                            변경
                                        </Button>
                                        {/*뤼소 버튼*/}
                                        <Button
                                            variant="secondary"
                                            onClick={togglePasswordEdit}
                                            size={"small"}
                                        >
                                            취소
                                        </Button>
                                    </div>
                                ) : (
                                    // 비밀번호 수정 모드 버튼
                                    <button
                                        className={styles.editButton}
                                        onClick={togglePasswordEdit}
                                    >
                                        <PencilSquareIcon className={styles.editIcon}/>
                                    </button>
                                )}
                            </div>
                        </div>
                        {isPasswordEditMode && (
                            <>
                                <div className={styles.passwordNotice}>
                                    {/*비밀번호 조건 안내 문구*/}
                                    <span>영어, 숫자, 특수문자가 섞인 8글자 이상이어야 합니다</span>
                                </div>
                                <div className={styles.item}>
                                    <span className={styles.label}>새 비밀번호</span>
                                    {/*비밀번호 입력*/}
                                    <input
                                        type="password"
                                        name="password"
                                        value={passwordInputs.password}
                                        onChange={handlePasswordChange}
                                        className={styles.passwordInput}
                                        placeholder="새 비밀번호 입력"
                                    />
                                </div>
                                <div className={styles.item}>
                                    <span className={styles.label}>비밀번호 확인</span>
                                    {/*비밀번호 확인 입력*/}
                                    <input
                                        type="password"
                                        name="confirmPassword"
                                        value={passwordInputs.confirmPassword}
                                        onChange={handlePasswordChange}
                                        className={styles.passwordInput}
                                        placeholder="비밀번호 확인"
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <div className={styles.headerContent}>
                        <BellIcon className={styles.sectionIcon}/>
                        <h2 className={styles.sectionTitle}>푸시 설정</h2>
                    </div>
                </div>
                <div className={styles.cardContent}>
                    <div className={styles.pushList}>
                        <div className={styles.pushItem}>
                            <span>자료 업로드</span>
                            <label className={styles.switch}>
                                {/*자료 업로드 푸시 설정 버튼*/}
                                <input
                                    type="checkbox"
                                    checked={data.uploadPush}
                                    value="UPLOAD"
                                    onChange={(e) => handleCheck(e.target.checked, e.target.value as PushSettings)}
                                    className={styles.switchInput}
                                />
                                <div className={styles.switchSlider}></div>
                            </label>
                        </div>
                        <div className={styles.pushItem}>
                            <span>추가 요청 프로젝트 처리</span>
                            <label className={styles.switch}>
                                {/*추가 요청 프로젝트 처리 푸시 설정 버튼*/}
                                <input
                                    type="checkbox"
                                    checked={data.projectConfirmPush}
                                    value="PROJECT_CONFIRM"
                                    onChange={(e) => handleCheck(e.target.checked, e.target.value as PushSettings)}
                                    className={styles.switchInput}
                                />
                                <div className={styles.switchSlider}></div>
                            </label>
                        </div>
                        <div className={styles.pushItem}>
                            <span>프로젝트 상태 변경</span>
                            <label className={styles.switch}>
                                {/*프로젝트 상태 변경 푸시 설정 버튼*/}
                                <input
                                    type="checkbox"
                                    checked={data.projectStatusPush}
                                    value="PROJECT_STATUS"
                                    onChange={(e) => handleCheck(e.target.checked, e.target.value as PushSettings)}
                                    className={styles.switchInput}
                                />
                                <div className={styles.switchSlider}></div>
                            </label>
                        </div>
                        <div className={styles.pushItem}>
                            <span>전자 세금 계산서 발행</span>
                            <label className={styles.switch}>
                                {/*전자 세금 계산서 발행 푸시 설정 버튼*/}
                                <input
                                    type="checkbox"
                                    checked={data.billingPush}
                                    value="BILLING"
                                    onChange={(e) => handleCheck(e.target.checked, e.target.value as PushSettings)}
                                    className={styles.switchInput}
                                />
                                <div className={styles.switchSlider}></div>
                            </label>
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <div className={styles.headerContent}>
                        <DocumentTextIcon className={styles.sectionIcon}/>
                        <h2 className={styles.sectionTitle}>약관</h2>
                    </div>
                </div>
                <div className={styles.cardContent}>
                    <div className={styles.content}>
                        <div className={styles.item}>
                            <span className={styles.label}>데이터 사업 규정</span>
                            {/*데이터 사업 규정 수정 버튼*/}
                            <button
                                className={styles.editButton}
                                onClick={handleTermModalOpen}
                            >
                                <PencilSquareIcon className={styles.editIcon}/>
                            </button>
                        </div>
                        <div className={styles.item}>
                            <span className={styles.label}>개인정보 처리 규정</span>
                            {/*개인정보 처리 규정 수정 버튼*/}
                            <button
                                className={styles.editButton}
                                onClick={handlePolicyModalOpen}
                            >
                                <PencilSquareIcon className={styles.editIcon}/>
                            </button>
                        </div>
                        <div className={styles.item}>
                            <span className={styles.label}>개인정보 수집·이용 동의</span>
                            {/*개인정보 수집·이용 동의 수정 버튼*/}
                            <button
                                className={styles.editButton}
                                onClick={handleRegisterModalOpen}
                            >
                                <PencilSquareIcon className={styles.editIcon}/>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            {/*<div className={styles.card}>*/}
            {/*    <div className={styles.cardHeader}>*/}
            {/*        <div className={styles.headerContent}>*/}
            {/*            <ChatBubbleLeftRightIcon className={styles.sectionIcon}/>*/}
            {/*            <h2 className={styles.sectionTitle}>챗봇</h2>*/}
            {/*        </div>*/}
            {/*    </div>*/}
            {/*    <div className={styles.cardContent}>*/}
            {/*        <div className={styles.item}>*/}
            {/*            <span className={styles.label}>챗봇 최초 출력 문장</span>*/}
            {/*            /!*챗봇 최초 출력 문장 수정 버튼*!/*/}
            {/*            <button*/}
            {/*                className={styles.editButton}*/}
            {/*                onClick={handleChatModalOpen}*/}
            {/*            >*/}
            {/*                <PencilSquareIcon className={styles.editIcon}/>*/}
            {/*            </button>*/}
            {/*        </div>*/}
            {/*    </div>*/}
            {/*</div>*/}
            {/*데이터 사업 규정 수정 팝업*/}
            <UpdateTermPopUp isOpen={isTermModalOpen} onClose={handleTermModalClose}/>
            {/*개인정보 처리 규정 수정 팝업*/}
            <UpdatePolicyPopUp isOpen={isPolicyModalOpen} onClose={handlePolicyModalClose}/>
            {/*개인정보 수집·이용 동의 수정 팝업*/}
            <UpdateRegisterPopUp isOpen={isRegisterModalOpen} onClose={handleRegisterModalClose}/>
            {/*챗봇 최초 출력 문장 수정 팝업*/}
            <UpdateChatBotPopUp isOpen={isChatModalOpen} onClose={handleChatModalClose}/>
            {/*이메일 수정 팝업*/}
            <UpdateEmailPopUp isOpen={isEmailModalOpen} onClose={handleEmailModalClose}/>
        </div>
    );
};

export default AdminSetting;